import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { History, Trash2, UserCog } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { api, errorMessage } from "@/lib/api";
import { fmtDate } from "@/lib/constants";

const DOT = { created: "bg-black", assigned: "bg-[#FACC15]", reassigned: "bg-[#FACC15]", unassigned: "bg-zinc-400", status: "bg-[#2563EB]", resolved: "bg-[#16A34A]", reopened: "bg-[#F97316]", duplicate: "bg-[#DC2626]", paused: "bg-[#7C3AED]", resumed: "bg-[#7C3AED]", note: "bg-zinc-600", priority: "bg-[#DC2626]", qrc: "bg-[#0891B2]" };

export function ActivityLog({ activity = [] }) {
  const items = [...activity].sort((a, b) => a.at.localeCompare(b.at));
  return (
    <div className="space-y-3 border-t-2 border-dashed border-zinc-300 pt-6" data-testid="activity-log">
      <h3 className="flex items-center gap-2 font-display text-lg font-bold"><History className="h-5 w-5" /> Activity <span className="text-sm font-normal text-zinc-500" data-testid="activity-count">({items.length})</span></h3>
      {!items.length ? <p className="text-sm text-zinc-500">No activity recorded yet.</p> : (
        <ol className="relative ml-2 space-y-3 border-l-2 border-zinc-200 pl-5">
          {items.map((a) => (
            <li key={a.id} className="relative text-sm" data-testid={`activity-${a.type}`}>
              <span className={`absolute -left-[27px] top-1 h-3 w-3 rounded-full border-2 border-white ${DOT[a.type] || "bg-zinc-500"}`} />
              <p className="font-semibold">{a.text}</p>
              <p className="text-xs text-zinc-500">{fmtDate(a.at)} · {a.by}</p>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

export function AdminControls({ ticket, onUpdated, onClose }) {
  const qc = useQueryClient();
  const [target, setTarget] = useState("");
  const [confirm, setConfirm] = useState(false);
  const { data: team = [] } = useQuery({ queryKey: ["team"], queryFn: () => api.get("/team").then((r) => r.data) });
  const refresh = () => ["tickets", "stats", "escalations", "admin-overview", "leaderboard", "my-performance"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
  const reassign = useMutation({
    mutationFn: () => api.post(`/tickets/${ticket.id}/reassign`, { user_id: target }).then((r) => r.data),
    onSuccess: (t) => { toast.success(`${t.ticket_id} now with ${t.assigned_to}`); onUpdated(t); setTarget(""); refresh(); },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const unassign = useMutation({
    mutationFn: () => api.post(`/tickets/${ticket.id}/unassign`).then((r) => r.data),
    onSuccess: (t) => { toast.success(`${t.ticket_id} back in the unassigned pool`); onUpdated(t); refresh(); },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const remove = useMutation({
    mutationFn: () => api.delete(`/tickets/${ticket.id}`).then((r) => r.data),
    onSuccess: (d) => { toast.success(`${d.deleted} deleted · recorded in audit log`); refresh(); onClose(); },
    onError: (e) => toast.error(errorMessage(e)),
  });
  return (
    <div className="space-y-3 rounded-xl border-2 border-black bg-zinc-50 p-4" data-testid="admin-controls">
      <h3 className="flex items-center gap-2 font-display text-base font-bold"><UserCog className="h-4 w-4" /> Admin · assignment</h3>
      <div className="flex flex-wrap items-center gap-2">
        <Select value={target} onValueChange={setTarget}>
          <SelectTrigger className="h-10 w-[220px] border-2 border-black bg-white text-sm font-semibold" data-testid="reassign-select"><SelectValue placeholder={ticket.assigned_to ? `Currently: ${ticket.assigned_to}` : "Assign to…"} /></SelectTrigger>
          <SelectContent>
            {team.filter((m) => m.id !== ticket.assigned_to_id && !m.on_leave).map((m) => <SelectItem key={m.id} value={m.id} data-testid={`reassign-option-${m.id}`}>{m.name} · {m.role}</SelectItem>)}
          </SelectContent>
        </Select>
        <button type="button" className="btn-dark !py-2 !text-sm" disabled={!target || reassign.isPending} onClick={() => reassign.mutate()} data-testid="reassign-button">
          {ticket.assigned_to ? "Reassign" : "Assign"}
        </button>
        {ticket.assigned_to && (
          <button type="button" className="btn-ghost !py-2 !text-sm" disabled={unassign.isPending} onClick={() => unassign.mutate()} data-testid="unassign-button">Unassign</button>
        )}
        <button type="button" className="btn-ghost ml-auto !border-[#DC2626] !py-2 !text-sm !text-[#B91C1C] hover:!bg-[#FEF2F2]" onClick={() => setConfirm(true)} data-testid="delete-ticket-button">
          <Trash2 className="h-4 w-4" /> Delete lead
        </button>
      </div>
      <AlertDialog open={confirm} onOpenChange={setConfirm}>
        <AlertDialogContent className="border-2 border-black" data-testid="delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display">Delete {ticket.ticket_id}?</AlertDialogTitle>
            <AlertDialogDescription>This removes the lead for everyone. Use it only for spam or test entries. The deletion (who, when, snapshot) is recorded in the audit log.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel-button">Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-[#DC2626] hover:bg-[#B91C1C]" onClick={() => remove.mutate()} data-testid="delete-confirm-button">Delete permanently</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
